import { find } from 'lodash';
import { Charterer, Checklists, Company, FlagState, ISMRelevantChecklist, LogbookKeptAdditionally, Owner, RecordFile, Signature, VesselClass, VesselParticulars, } from "../../entities";
import { getAuthorRefForGateway, getAuthorRefFromGateway } from './ParsingUtils';
export default class VesselParticularsParser {
    static extensionName = 'FileAttachments';
    static getFileAttachmentExtension(vesselParticulars) {
        return {
            Manufacturer: 'ANZ',
            Name: this.extensionName,
            Value: vesselParticulars.staticDocuments.map(({ _id: fileId }) => fileId),
        };
    }
    static getFiles(gatewayVesselParticulars) {
        const fileAttachmentExtension = find(gatewayVesselParticulars.Extensions, { Name: this.extensionName });
        if (!fileAttachmentExtension) {
            return [];
        }
        const files = fileAttachmentExtension.Value.map((fileId) => RecordFile.createRef(fileId));
        return files;
    }
    static toGateway(vessel, app) {
        return {
            AdministrationForDocumentOfCompliance: vessel.administrationForDocumentOfCompliance,
            AdministrationForSafetyManagementCertificate: vessel.administrationForSafetyManagementCertificate,
            AdministrationForSecurityCertificate: vessel.administrationForSecurityCertificate,
            AuditorForDocumentOfCompliance: vessel.auditorForDocumentOfCompliance,
            DateOfIssuanceForDocumentOfCompliance: vessel.dateOfIssuanceForDocumentOfCompliance,
            DateOfExpiryForDocumentOfCompliance: vessel.dateOfExpiryForDocumentOfCompliance,
            DateOfIssuanceForSafetyManagementCertificate: vessel.dateOfIssuanceForSafetyManagementCertificate,
            DateOfExpiryForSafetyManagementCertificate: vessel.dateOfExpiryForSafetyManagementCertificate,
            DateOfIssuanceForInternationalShipSecurityCertificate: vessel.dateOfIssuanceForInternationalShipSecurityCertificate,
            DateOfExpiryForInternationalShipSecurityCertificate: vessel.dateOfExpiryForInternationalShipSecurityCertificate,
            AuditorForSafetyManagementCertificate: vessel.auditorForSafetyManagementCertificate,
            AuditorForSecurityCertificate: vessel.auditorForSecurityCertificate,
            GrossTonnage: vessel.grossTonnage,
            NetTonnage: vessel.netTonnage,
            LengthOverall: vessel.lengthOverall,
            BreadthOverall: vessel.breadthOverall,
            WorkingLanguage: vessel.workingLanguage,
            CallSign: vessel.callSign,
            MmsiNumber: vessel.mmsiNumber,
            ShipType: vessel.shipType,
            SeaArea: vessel.seaArea,
            EquipmentAvailability: vessel.equipmentAvailability,
            Extensions: [this.getFileAttachmentExtension(vessel)],
            CompanyDetails: vessel.companyDetails,
            Charterers: vessel.charterers.map((charterer) => {
                return {
                    Address: charterer.address,
                    Name: charterer.name,
                };
            }),
            Classes: vessel.classes.map((vesselClass) => {
                return vesselClass.vesselClass;
            }),
            CompanyResponsibleForVesselManagement: vessel.companyResponsibleForVesselManagement.map((company) => {
                return {
                    Address: company.address,
                    AddressesForSafetyManagement: company.addressesForSafetyManagement,
                    Name: company.name,
                    IdentificationNumber: company.identificationNumber,
                };
            }),
            FlagStates: vessel.flagStates.map((flagState) => {
                return {
                    FlagRegistrationDate: flagState.flagRegistrationDate,
                    FlagState: flagState.flagState,
                    CeaseDateOfFlagstateRegistration: flagState.ceaseDateOfFlagstateRegistration,
                };
            }),
            Id: vessel._id,
            PortOfRegistration: vessel.portOfRegistration,
            SignedBy: getAuthorRefForGateway(vessel.author, app),
            VesselIMO: vessel.vesselIMO,
            VesselName: vessel.vesselName,
            VesselOwners: vessel.vesselOwners.map((owner) => {
                return {
                    Address: owner.address,
                    Name: owner.name,
                    IMORegisteredOwnerNumber: owner.imoRegisteredOwnerNumber,
                };
            }),
            LogbooksKeptAdditionally: vessel.logbooksKeptAdditionally.map((logbooksKeptAdditionally) => {
                return logbooksKeptAdditionally.logbookTitle;
            }),
            ISMRelevantChecklists: vessel.ismRelevantChecklists
                ? vessel.ismRelevantChecklists.map((ismRelevantChecklists) => {
                    return ismRelevantChecklists.name;
                })
                : undefined,
            Checklists: vessel.checklists?.map((cl) => ({
                Name: cl.name,
                Type: cl.type,
            })),
        };
    }
    static fromGateway(vessel, index, timestamp, app, signature) {
        const newVesselParticular = new VesselParticulars({
            version: index,
            author: getAuthorRefFromGateway(vessel.SignedBy, app),
            administrationForDocumentOfCompliance: vessel.AdministrationForDocumentOfCompliance,
            administrationForSafetyManagementCertificate: vessel.AdministrationForSafetyManagementCertificate,
            auditorForDocumentOfCompliance: vessel.AuditorForDocumentOfCompliance,
            auditorForSafetyManagementCertificate: vessel.AuditorForSafetyManagementCertificate,
            auditorForSecurityCertificate: vessel.AuditorForSecurityCertificate,
            dateOfIssuanceForDocumentOfCompliance: vessel.DateOfIssuanceForDocumentOfCompliance,
            dateOfExpiryForDocumentOfCompliance: vessel.DateOfExpiryForDocumentOfCompliance,
            dateOfIssuanceForSafetyManagementCertificate: vessel.DateOfIssuanceForSafetyManagementCertificate,
            dateOfExpiryForSafetyManagementCertificate: vessel.DateOfExpiryForSafetyManagementCertificate,
            dateOfIssuanceForInternationalShipSecurityCertificate: vessel.DateOfIssuanceForInternationalShipSecurityCertificate,
            dateOfExpiryForInternationalShipSecurityCertificate: vessel.DateOfExpiryForInternationalShipSecurityCertificate,
            grossTonnage: vessel.GrossTonnage,
            netTonnage: vessel.NetTonnage,
            lengthOverall: vessel.LengthOverall,
            breadthOverall: vessel.BreadthOverall,
            workingLanguage: vessel.WorkingLanguage,
            callSign: vessel.CallSign,
            mmsiNumber: vessel.MmsiNumber,
            shipType: vessel.ShipType,
            seaArea: vessel.SeaArea,
            equipmentAvailability: vessel.EquipmentAvailability,
            companyDetails: vessel.CompanyDetails,
            charterers: vessel.Charterers.map((charterer) => {
                return new Charterer(charterer.Address, charterer.Name);
            }),
            classes: vessel.Classes.map((c) => {
                return new VesselClass(c);
            }),
            companyResponsibleForVesselManagement: vessel.CompanyResponsibleForVesselManagement
                ? vessel.CompanyResponsibleForVesselManagement.map((company) => {
                    return new Company(company.Address, company.Name, company.AddressesForSafetyManagement, company.IdentificationNumber);
                })
                : [],
            flagStates: vessel.FlagStates.map((flagState) => {
                return new FlagState(flagState.FlagRegistrationDate, flagState.FlagState, flagState.CeaseDateOfFlagstateRegistration);
            }),
            portOfRegistration: vessel.PortOfRegistration,
            vesselIMO: vessel.VesselIMO,
            vesselName: vessel.VesselName,
            vesselOwners: vessel.VesselOwners.map((owner) => {
                return new Owner(owner.Address, owner.Name, owner.IMORegisteredOwnerNumber);
            }),
            logbooksKeptAdditionally: (vessel.LogbooksKeptAdditionally || []).map((logbook) => {
                return new LogbookKeptAdditionally(logbook);
            }),
            ismRelevantChecklists: vessel.ISMRelevantChecklists
                ? vessel.ISMRelevantChecklists.map((ismRelevantChecklist) => {
                    return new ISMRelevantChecklist(ismRelevantChecklist);
                })
                : undefined,
            checklists: vessel.Checklists
                ? vessel.Checklists.map((checklist) => {
                    return new Checklists(checklist.Name, checklist.Type);
                })
                : undefined,
        });
        if (signature &&
            vessel.SignInfo &&
            vessel.SignInfo.SignatureMethod &&
            vessel.SignInfo.KeyInfo &&
            vessel.SignInfo.KeyInfo.KeyName) {
            newVesselParticular.signature = new Signature(signature, vessel.SignInfo.KeyInfo.KeyName, vessel.SignInfo.SignatureMethod);
        }
        const documents = this.getFiles(vessel);
        Object.assign(newVesselParticular, {
            _id: vessel.Id,
            createdTimestamp: timestamp,
            staticDocuments: documents,
        });
        return newVesselParticular;
    }
}
//# sourceMappingURL=VesselParticularsParser.js.map